"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Activity, CheckCircle2, XCircle, Clock, Loader2, AlertTriangle, Minus } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { fetchCycleProgress, type CycleProgressState, type CycleSymbolStatus } from "@/lib/agent-force"

/** Always poll at this rate — the endpoint is in-memory with no DB/MT5 cost. */
const POLL_MS = 2_500

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60_000)
  const s = Math.round((ms % 60_000) / 1000)
  return `${m}m ${s}s`
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

function phaseLabel(phase: CycleProgressState["phase"]): string {
  switch (phase) {
    case "init":            return "Initialising…"
    case "exit_management": return "Checking exits…"
    case "symbol_analysis": return "Analysing symbols…"
    case "selection":       return "Selecting best candidate…"
    case "execution":       return "Executing trade…"
    case "done":            return "Cycle complete"
    case "error":           return "Cycle error"
    default:                return phase
  }
}

function SymbolChip({ item }: { item: CycleSymbolStatus }) {
  const isCandidate = item.is_candidate
  const isError     = item.status === "error"
  const score       = typeof item.score === "number" ? item.score : null

  let cls = "border-border/50 bg-secondary/20 text-muted-foreground/70"
  if (isCandidate) cls = "border-positive/50 bg-positive/10 text-positive"
  else if (isError) cls = "border-destructive/40 bg-destructive/5 text-destructive"

  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-mono leading-none ${cls}`}
      title={item.reason ?? item.status}
    >
      {isCandidate && <CheckCircle2 className="h-2.5 w-2.5 shrink-0" />}
      {isError     && <XCircle      className="h-2.5 w-2.5 shrink-0" />}
      {!isCandidate && !isError && <Minus className="h-2.5 w-2.5 shrink-0 opacity-40" />}
      <span>{item.symbol}</span>
      {score !== null && <span className="opacity-60">{score.toFixed(2)}</span>}
      {item.reason && !isCandidate && (
        <span className="opacity-50 max-w-[72px] truncate">{item.reason.replace(/_/g, " ")}</span>
      )}
    </span>
  )
}

export function CycleProgressPanel() {
  const [progress, setProgress] = useState<CycleProgressState | null>(null)
  const [displayMs, setDisplayMs] = useState(0)
  const tickRef        = useRef<ReturnType<typeof setInterval> | null>(null)
  const startWallRef   = useRef<number | null>(null)
  const baseElapsedRef = useRef<number>(0)

  const isActive = progress !== null && !progress.done && progress.phase !== "idle"

  const poll = useCallback(async () => {
    try {
      const p = await fetchCycleProgress()
      setProgress(p)
    } catch {
      // endpoint may not exist yet in older backend versions
    }
  }, [])

  // Constant poll — 2.5 s regardless of state.
  useEffect(() => {
    void poll()
    const id = setInterval(() => void poll(), POLL_MS)
    return () => clearInterval(id)
  }, [poll])

  // Local sub-second display timer so elapsed time ticks smoothly between polls.
  useEffect(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
    if (isActive) {
      startWallRef.current   = Date.now()
      baseElapsedRef.current = progress?.elapsed_ms ?? 0
      setDisplayMs(baseElapsedRef.current)
      tickRef.current = setInterval(() => {
        const delta = startWallRef.current ? Date.now() - startWallRef.current : 0
        setDisplayMs(baseElapsedRef.current + delta)
      }, 200)
    } else if (progress?.done) {
      setDisplayMs(progress.elapsed_ms ?? 0)
    }
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, progress?.done])

  // Never ran — hide
  if (!progress) return null
  if (progress.phase === "idle" && !progress.started_at) return null

  const isDone          = progress.done
  const isError         = progress.phase === "error" || progress.result_status === "error"
  const isNoTrade       = progress.result_status === "no_trade"
  const isTradeSelected = progress.result_status === "trade_selected"
  const pct             = Math.max(0, Math.min(100, progress.pct))

  const barColor = isError
    ? "bg-destructive"
    : isTradeSelected
    ? "bg-positive"
    : isNoTrade
    ? "bg-warning"
    : "bg-primary"

  // ── COMPLETED STATE ── full panel, bar dimmed/static ───────────────────────
  if (isDone) {
    return (
      <div className="mt-4 border-t border-border pt-4 space-y-3">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex items-center gap-2 min-w-0">
            {isError ? (
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
            ) : isTradeSelected ? (
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-positive" />
            ) : (
              <Activity className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
            )}
            <p className="text-xs font-semibold text-foreground/80">Last cycle</p>
            {isTradeSelected && (
              <Badge variant="outline" className="text-[10px] text-positive border-positive/40 bg-positive/10">
                Trade selected{progress.selected_symbol ? ` · ${progress.selected_symbol}` : ""}
              </Badge>
            )}
            {isNoTrade && (
              <Badge variant="outline" className="text-[10px] text-muted-foreground border-border">
                No trade
              </Badge>
            )}
            {isError && (
              <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30 bg-destructive/5">
                Error
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground">
              <Clock className="h-3 w-3" />
              {formatMs(progress.elapsed_ms)}
            </span>
            {progress.started_at && (
              <span className="text-[10px] text-muted-foreground/50">
                {formatWhen(progress.started_at)}
              </span>
            )}
          </div>
        </div>

        {/* Static bar — full width, dimmed to show it's not live */}
        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-secondary/40">
          <div className={`h-full w-full rounded-full opacity-30 ${barColor}`} />
        </div>
        <p className="text-[10px] text-muted-foreground/40 text-right">100% — completed</p>

        {/* Symbol chips */}
        {progress.symbol_statuses.length > 0 && (
          <div className="space-y-1.5">
            {progress.symbols_total > 0 && (
              <p className="text-[10px] text-muted-foreground">
                Symbols: {progress.symbols_done}/{progress.symbols_total} analysed
                {progress.symbols?.length > 0 && (
                  <span className="ml-1 opacity-55">({progress.symbols.join(", ")})</span>
                )}
              </p>
            )}
            <div className="flex flex-wrap gap-1">
              {progress.symbol_statuses.map((item) => (
                <SymbolChip key={item.symbol} item={item} />
              ))}
            </div>
          </div>
        )}

        {isError && progress.error && (
          <p className="text-[11px] text-destructive truncate">{progress.error}</p>
        )}
      </div>
    )
  }

  // ── ACTIVE STATE ── full animated panel ─────────────────────────────────────
  return (
    <div className="mt-4 border-t border-border pt-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Loader2 className="h-3.5 w-3.5 shrink-0 text-primary animate-spin" />
          <div className="min-w-0">
            <p className="text-[10px] font-medium text-muted-foreground leading-none mb-0.5">Agent cycle</p>
            <p className="text-xs font-semibold text-foreground/90 truncate leading-none">
              {phaseLabel(progress.phase)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground">
            <Clock className="h-3 w-3" />
            {formatMs(displayMs)}
          </span>
          <Badge variant="outline" className="text-[10px] text-primary border-primary/40 bg-primary/10">
            Cycle live
          </Badge>
        </div>
      </div>

      {/* Animated bar */}
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary/40">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Symbol progress */}
      {progress.symbols_total > 0 && (
        <p className="text-[10px] text-muted-foreground">
          Symbols: {progress.symbols_done}/{progress.symbols_total} analysed
          {progress.symbols?.length > 0 && (
            <span className="ml-1 opacity-55">({progress.symbols.join(", ")})</span>
          )}
        </p>
      )}
      {progress.symbol_statuses.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {progress.symbol_statuses.map((item) => (
            <SymbolChip key={item.symbol} item={item} />
          ))}
        </div>
      )}

      {/* Selected symbol (execution phase) */}
      {progress.selected_symbol && (
        <p className="text-[11px] font-mono text-positive">
          Executing: <span className="font-semibold">{progress.selected_symbol}</span>
        </p>
      )}

      <p className="text-[10px] text-muted-foreground/50 text-right">{pct}% complete</p>
    </div>
  )
}
